import { ReactElement } from 'react';
import { Cell } from 'react-table';

import { DynamicFilterArgs, DynamicFiltersFields } from '../../../../apis/graphql/reports/standardDelivery/types';
import { AdProduct, MinMaxNumberValues, Publisher } from '../../../../apis/graphql/types';
import { MinMaxFilter, MultiSelectFilter } from '../../../../components/BlueTheme/Filter';
import { highlightSearch } from '../../../../components/BlueTheme/MultiSelect/utils';
import { defaultCellContent } from '../../../../constants';
import { CustomColumnConfig } from '../../../../types/table';
import { formatDateToMMDDYYYY, formatNumber, formatToNonMicros } from '../../../../utils/formatting';
import { LINE_ITEM_END_DATE_COLUMN_ID, LINE_ITEM_NAME_COLUMN_ID, LINE_ITEM_START_DATE_COLUMN_ID } from '../constants';
import { LineEndFilter, LineStartFilter } from '../Filters';
import { publisherOptions } from '../Shared/ReportForm/constants';
import { capitalizeFirstLetter } from '../Shared/utils';
import { StyledNumberWrapper, StyledSubHeader } from './styled';
import { StandardDeliveryReportTableData } from './types';

export const getStandardDeliveryReportColumns = (
  onFilterChanged: ({ id, value }: DynamicFilterArgs<DynamicFiltersFields>) => void,
  is3PDelivery?: boolean
): CustomColumnConfig<StandardDeliveryReportTableData>[] => {
  const baseFilters: CustomColumnConfig<StandardDeliveryReportTableData>[] = [
    {
      Header: '',
      id: 'generalData',
      columns: [
        {
          Header: 'Line Item Name',
          id: LINE_ITEM_NAME_COLUMN_ID,
          accessor: 'lineItemName',
          minWidth: 350,
          tableBorders: {
            columns: {
              right: true,
            },
            subHeaders: {
              right: true,
            },
          },
          disableFilters: true,
          sticky: true,
          Cell: (cell: Cell<StandardDeliveryReportTableData>): ReactElement | string =>
            highlightSearch(cell.row.original.lineItemName, cell.column.filterValue || ''),
        },
        {
          Header: 'Ad Product',
          accessor: 'adProduct',
          id: 'adProduct',
          minWidth: 90,
          Filter: ({ column: { setFilter, filterValue } }): JSX.Element => {
            const handleFilter = (value?: AdProduct[]): void => {
              onFilterChanged({ id: 'adProduct', value: value });
              setFilter(value);
            };
            return (
              <MultiSelectFilter
                elementId="adProductFilter"
                allItems={[{ label: capitalizeFirstLetter(AdProduct.Video), value: AdProduct.Video }]}
                selectedItems={filterValue || []}
                onChange={(el): void => handleFilter(el as AdProduct[])}
              />
            );
          },
          Cell: ({ value }: Cell<StandardDeliveryReportTableData>): string =>
            value ? capitalizeFirstLetter(value) : '',
        },
        {
          Header: 'Publisher',
          accessor: 'publisher',
          id: 'publisher',
          minWidth: 90,
          Filter: ({ column: { setFilter, filterValue } }): JSX.Element => {
            const handleFilter = (value?: Publisher[]): void => {
              onFilterChanged({ id: 'publisher', value: value });
              setFilter(value);
            };
            return (
              <MultiSelectFilter
                elementId="publisherFilter"
                allItems={publisherOptions}
                selectedItems={filterValue || []}
                onChange={(el): void => handleFilter(el as Publisher[])}
              />
            );
          },
          disableSortBy: true,
        },
        {
          Header: 'Campaign Name',
          accessor: 'campaignName',
          id: 'campaignName',
          minWidth: 300,
          disableFilters: true,
        },
        {
          Header: 'Line Start Date',
          id: LINE_ITEM_START_DATE_COLUMN_ID,
          accessor: 'startDate',
          Filter: ({ column: { setFilter, filterValue }, state: { filters } }): JSX.Element => (
            <LineStartFilter
              setFilter={setFilter}
              filterValue={filterValue}
              filters={filters}
              onFilterChanged={(value): void => {
                onFilterChanged({
                  id: 'startDate',
                  value,
                });
              }}
            />
          ),
          Cell: (cell: Cell<StandardDeliveryReportTableData>): string =>
            cell.row.original.startDate ? formatDateToMMDDYYYY(cell.row.original.startDate) : defaultCellContent,
          minWidth: 120,
        },
        {
          Header: 'Line End Date',
          accessor: 'endDate',
          id: LINE_ITEM_END_DATE_COLUMN_ID,
          Filter: ({ column: { filterValue, setFilter }, state: { filters } }): JSX.Element => (
            <LineEndFilter
              setFilter={setFilter}
              filterValue={filterValue}
              filters={filters}
              onFilterChanged={(value): void => {
                onFilterChanged({
                  id: 'endDate',
                  value,
                });
              }}
            />
          ),
          Cell: (cell: Cell<StandardDeliveryReportTableData>): string =>
            cell.row.original.endDate ? formatDateToMMDDYYYY(cell.row.original.endDate) : defaultCellContent,
          minWidth: 120,
        },
        {
          Header: 'CPM',
          accessor: 'cpm',
          minWidth: 80,
          id: 'cpm',
          Filter: ({ column: { setFilter, filterValue } }): JSX.Element => {
            const handleFilter = (value: MinMaxNumberValues): void => {
              onFilterChanged({ id: 'cpm', value });
              setFilter(value);
            };
            return (
              <MinMaxFilter
                elementId="cpmFilter"
                showCurrency={true}
                choosenData={filterValue}
                onChange={handleFilter}
              />
            );
          },
          Cell: (cell: Cell<StandardDeliveryReportTableData>): string =>
            cell.row.original.cpm
              ? formatNumber({
                  figure: formatToNonMicros(cell.row.original.cpm),
                  dollarValue: true,
                })
              : defaultCellContent,
        },
        {
          Header: 'Booked Spend',
          accessor: 'revenue',
          id: 'revenue',
          minWidth: 90,
          Filter: ({ column: { setFilter, filterValue } }): JSX.Element => {
            const handleFilter = (value: MinMaxNumberValues): void => {
              onFilterChanged({ id: 'revenue', value });
              setFilter(value);
            };
            return (
              <MinMaxFilter
                elementId="revenueFilter"
                showCurrency={true}
                choosenData={filterValue}
                onChange={handleFilter}
              />
            );
          },
          Cell: (cell: Cell<StandardDeliveryReportTableData>): JSX.Element => (
            <StyledNumberWrapper>
              {cell.row.original.revenue
                ? formatNumber({
                    figure: formatToNonMicros(cell.row.original.revenue),
                    dollarValue: true,
                  })
                : defaultCellContent}
            </StyledNumberWrapper>
          ),
        },
        {
          Header: 'Order Goal',
          accessor: 'orderGoal',
          id: 'orderGoal',
          minWidth: 90,
          Filter: ({ column: { setFilter, filterValue } }): JSX.Element => {
            const handleFilter = (value: MinMaxNumberValues): void => {
              onFilterChanged({ id: 'orderGoal', value });
              setFilter(value);
            };
            return <MinMaxFilter elementId="orderGoalFilter" choosenData={filterValue} onChange={handleFilter} />;
          },
          Cell: ({
            row: {
              original: { orderGoal },
            },
          }: Cell<StandardDeliveryReportTableData>): JSX.Element => (
            <StyledNumberWrapper>
              {orderGoal ? formatNumber({ figure: orderGoal }) : defaultCellContent}
            </StyledNumberWrapper>
          ),
        },
        {
          Header: 'Account Executive',
          accessor: 'accountExecutive',
          id: 'accountExecutive',
          minWidth: 150,
          disableFilters: true,
        },
        {
          Header: 'Account Manager',
          accessor: 'accountManager',
          id: 'accountManager',
          minWidth: 150,
          disableFilters: true,
          tableBorders: {
            subHeaders: {
              right: true,
            },
          },
        },
      ],
    },
    {
      Header: 'Disney 1P Data',
      columns: [
        {
          Header: 'Impressions',
          accessor: 'impressions',
          id: 'impressions',
          minWidth: 110,
          disableSortBy: true,
          disableFilters: true,
          Cell: ({
            row: {
              original: { impressions },
            },
          }: Cell<StandardDeliveryReportTableData>): JSX.Element => (
            <StyledNumberWrapper>
              {impressions
                ? formatNumber({
                    figure: impressions,
                  })
                : defaultCellContent}
            </StyledNumberWrapper>
          ),
        },
        {
          Header: 'Completions',
          minWidth: 110,
          accessor: 'completions',
          id: 'completions',
          disableSortBy: true,
          disableFilters: true,
          Cell: (cell: Cell<StandardDeliveryReportTableData>): JSX.Element => (
            <StyledNumberWrapper>
              {cell.row.original.completions
                ? formatNumber({
                    figure: cell.row.original.completions,
                  })
                : defaultCellContent}
            </StyledNumberWrapper>
          ),
        },
        {
          Header: 'VCR%',
          minWidth: 80,
          accessor: 'vcrPercentage',
          id: 'vcrPercentage',
          disableSortBy: true,
          tableBorders: {
            subHeaders: {
              right: true,
            },
          },
          disableFilters: true,
          Cell: (cell: Cell<StandardDeliveryReportTableData>): JSX.Element => (
            <StyledNumberWrapper>
              {cell.row.original.vcrPercentage
                ? formatNumber({
                    figure: cell.row.original.vcrPercentage,
                    percentageValue: true,
                  })
                : defaultCellContent}
            </StyledNumberWrapper>
          ),
        },
      ],
    },
    {
      Header: 'Estimated Billable',
      columns: [
        {
          Header: 'Over/Under Delivery',
          accessor: 'overUnderDelivery',
          id: 'overUnderDelivery',
          minWidth: 120,
          disableSortBy: true,
          disableFilters: true,
          Cell: (cell: Cell<StandardDeliveryReportTableData>): JSX.Element => (
            <StyledNumberWrapper>
              {cell.row.original.overUnderDelivery
                ? formatNumber({
                    figure: cell.row.original.overUnderDelivery,
                  })
                : defaultCellContent}
            </StyledNumberWrapper>
          ),
        },
        {
          Header: 'Over/Under Spend',
          accessor: 'overUnderSpend',
          id: 'overUnderSpend',
          minWidth: 120,
          disableSortBy: true,
          disableFilters: true,
          Cell: (cell: Cell<StandardDeliveryReportTableData>): JSX.Element => (
            <StyledNumberWrapper>
              {cell.row.original.overUnderSpend
                ? formatNumber({
                    figure: cell.row.original.overUnderSpend,
                    dollarValue: true,
                  })
                : defaultCellContent}
            </StyledNumberWrapper>
          ),
        },
        {
          Header: 'Estimated Billable',
          accessor: 'estimatedBillable',
          id: 'estimatedBillable',
          minWidth: 100,
          disableSortBy: true,
          disableFilters: true,
          Cell: (cell: Cell<StandardDeliveryReportTableData>): JSX.Element => (
            <StyledNumberWrapper>
              {cell.row.original.estimatedBillable
                ? formatNumber({
                    figure: cell.row.original.estimatedBillable,
                    dollarValue: true,
                  })
                : defaultCellContent}
            </StyledNumberWrapper>
          ),
        },
      ],
    },
  ];

  const threePDeliveryColumn: CustomColumnConfig<StandardDeliveryReportTableData> = {
    Header: '3P Data',
    id: 'thirdPartyGrossBillableImpressions',
    maxWidth: 120,
    width: 120,
    columns: [
      {
        Header: <StyledSubHeader>3P Gross Billable Impressions</StyledSubHeader>,
        accessor: 'thirdPartyGrossBillableImpressions',
        id: 'thirdPartyGrossBillableImpressions',
        tableBorders: {
          subHeaders: {
            left: true,
          },
        },
        disableSortBy: true,
        disableFilters: true,
        Cell: (cell: Cell<StandardDeliveryReportTableData>): JSX.Element => (
          <StyledNumberWrapper>
            {cell.row.original.thirdPartyGrossBillableImpressions
              ? formatNumber({
                  figure: cell.row.original.thirdPartyGrossBillableImpressions,
                })
              : defaultCellContent}
          </StyledNumberWrapper>
        ),
      },
    ],
  };

  is3PDelivery && baseFilters.splice(2, 0, threePDeliveryColumn);

  return baseFilters;
};
